import { LocalStorageKeys } from './consts';
import { useInMemoryStorage } from './hooks/useInMemoryStorage';
import type { TranscriptExpectation } from './models/transcriptExpectations';
import { EnrichedBlockOutput } from './stores/block_output_logs_store';

// Helper function to get userId from in-memory storage
const getUserId = () => {
  return useInMemoryStorage.getState().accountMe?.user?.id;
};

const getLocalStorageKey = (get, key: string) => {
  // First try to get userId from the in-memory storage
  const userId = getUserId();

  if (!userId) {
    return null;
  }

  return `@imprv.ai.${userId}::${key}`;
};

const writeToLocalStorage = (get, key: string, value: any) => {
  const localStorageKey = getLocalStorageKey(get, key);
  if (!localStorageKey) {
    return;
  }

  if (typeof localStorage === 'undefined') {
    throw new Error('localStorage is not available');
  }

  localStorage.setItem(localStorageKey, value);
};

const readFromLocalStorage = (get, key: string) => {
  const localStorageKey = getLocalStorageKey(get, key);

  if (!localStorageKey) {
    return null;
  }

  if (typeof localStorage === 'undefined') {
    throw new Error('localStorage is not available');
  }

  return localStorage.getItem(localStorageKey);
};

const removeFromLocalStorage = (get, key: string) => {
  const localStorageKey = getLocalStorageKey(get, key);

  if (typeof localStorage === 'undefined') {
    throw new Error('localStorage is not available');
  }

  localStorage.removeItem(localStorageKey);
};

export const isClient = (): boolean => {
  return typeof window === 'object';
};

// LocalStorageKeys.LAST_SCHEDULE_QUEUE_DATE: state.lastScheduleQueueDate
export const setLastScheduleQueueDate = (get, set) => (date: string) => {
  if (!isClient()) {
    return;
  }

  writeToLocalStorage(get, LocalStorageKeys.LAST_SCHEDULE_QUEUE_DATE, date);
  set({ lastScheduleQueueDate: date });
};

export const getLastScheduleQueueDate = (get, set) => () => {
  // Return the current value from store state, don't read from localStorage during render
  return get().lastScheduleQueueDate;
};

export const initLastScheduleQueueDate = (get, set) => () => {
  if (!isClient()) {
    return null;
  }

  const lastScheduleQueueDateLocalStorage = readFromLocalStorage(
    get,
    LocalStorageKeys.LAST_SCHEDULE_QUEUE_DATE
  );

  const currentState = get();
  if (!currentState) {
    return null;
  }

  // Only update if the value is different to prevent unnecessary re-renders
  if (currentState.lastScheduleQueueDate !== lastScheduleQueueDateLocalStorage) {
    set({ lastScheduleQueueDate: lastScheduleQueueDateLocalStorage });
  }
  return lastScheduleQueueDateLocalStorage;
};

// LocalStorageKeys.INTRO_IS_DONE: state.introIsDone
export const setIntroIsDone = (get, set) => (isDone: boolean) => {
  if (!isClient()) {
    return;
  }

  if (isDone) {
    writeToLocalStorage(get, LocalStorageKeys.INTRO_IS_DONE, isDone.toString());
    set({ introIsDone: isDone });
  } else {
    removeFromLocalStorage(get, LocalStorageKeys.INTRO_IS_DONE);
    set({ introIsDone: false });
  }
};

export const getIntroIsDone = (get, set) => () => {
  // Return the current value from store state, don't read from localStorage during render
  return get().introIsDone;
};

export const initIntroIsDone = (get, set) => () => {
  if (!isClient()) {
    return false;
  }

  const introIsDone = readFromLocalStorage(get, LocalStorageKeys.INTRO_IS_DONE) === 'true';

  const currentState = get();
  if (!currentState) {
    return false;
  }

  // Only update if the value is different to prevent unnecessary re-renders
  if (currentState.introIsDone !== introIsDone) {
    set({ introIsDone: introIsDone });
  }
  return introIsDone;
};

// LocalStorageKeys.VIDEO_PLAYBACK_SPEED: state.videoPlaybackSpeed
export const setVideoPlaybackSpeed = (get, set) => (speed: number) => {
  if (!isClient()) {
    return;
  }

  writeToLocalStorage(get, LocalStorageKeys.VIDEO_PLAYBACK_SPEED, speed.toString());
  set({ videoPlaybackSpeed: speed });
};

export const getVideoPlaybackSpeed = (get, set) => () => {
  // Return the current value from store state, don't read from localStorage during render
  return get().videoPlaybackSpeed;
};

export const initVideoPlaybackSpeed = (get, set) => () => {
  if (!isClient()) {
    return 1;
  }

  try {
    const savedSpeed = readFromLocalStorage(get, LocalStorageKeys.VIDEO_PLAYBACK_SPEED) || '1';
    let speed = Number.parseFloat(savedSpeed);
    if (isNaN(speed) || ![0.5, 0.75, 1, 1.25, 1.5, 2].includes(speed)) {
      speed = 1;
    }

    const currentState = get();
    if (!currentState) {
      return speed;
    }

    // Only update if the value is different to prevent unnecessary re-renders
    if (currentState.videoPlaybackSpeed !== speed) {
      set({ videoPlaybackSpeed: speed });
    }
    return speed;
  } catch (error) {
    console.error('Error loading video playback speed:', error);
    const currentState = get();
    if (currentState && currentState.videoPlaybackSpeed !== 1) {
      set({ videoPlaybackSpeed: 1 });
    }
    return 1;
  }
};

// LocalStorageKeys.PENDING_BLOCK_OUTPUTS: pending block outputs persistence
export const savePendingBlockOutputs = (outputs: EnrichedBlockOutput[]): void => {
  if (!isClient() || outputs.length === 0) return;

  try {
    // Merge with existing LS data to avoid overwriting previous failed outputs
    const raw = readFromLocalStorage(null, LocalStorageKeys.PENDING_BLOCK_OUTPUTS);
    let merged = outputs;
    if (raw) {
      const existing: EnrichedBlockOutput[] = JSON.parse(raw).outputs ?? [];
      const existingKeys = new Set(existing.map((o) => o.idempotencyKey));
      const newOutputs = outputs.filter((o) => !existingKeys.has(o.idempotencyKey));
      merged = [...existing, ...newOutputs];
    }

    const data = JSON.stringify({ timestamp: Date.now(), outputs: merged });
    writeToLocalStorage(null, LocalStorageKeys.PENDING_BLOCK_OUTPUTS, data);
  } catch {
    removeFromLocalStorage(null, LocalStorageKeys.PENDING_BLOCK_OUTPUTS);
  }
};

export const clearPendingBlockOutputs = (): void => {
  if (!isClient()) return;
  removeFromLocalStorage(null, LocalStorageKeys.PENDING_BLOCK_OUTPUTS);
};

export const loadPendingBlockOutputs = (): EnrichedBlockOutput[] => {
  if (!isClient()) return [];

  try {
    const raw = readFromLocalStorage(null, LocalStorageKeys.PENDING_BLOCK_OUTPUTS);
    if (!raw) return [];

    const data = JSON.parse(raw);
    return data.outputs ?? [];
  } catch {
    removeFromLocalStorage(null, LocalStorageKeys.PENDING_BLOCK_OUTPUTS);
    return [];
  }
};

const isValidTranscriptExpectation = (value: unknown): boolean => {
  const expectation = value as TranscriptExpectation;
  return (
    typeof expectation?.taskEntityId === 'string' &&
    typeof expectation.returnUrl === 'string' &&
    expectation.returnUrl.startsWith('/') &&
    !expectation.returnUrl.startsWith('//') &&
    !expectation.returnUrl.startsWith('/\\') &&
    typeof expectation.filename === 'string' &&
    Number.isFinite(expectation.createdAt)
  );
};

const discardTranscriptExpectations = (): void => {
  try {
    removeFromLocalStorage(null, LocalStorageKeys.TRANSCRIPT_EXPECTATIONS);
  } catch {
    // storage is unavailable, nothing left to clean up
  }
};

export const saveTranscriptExpectations = (
  expectations: Record<number, TranscriptExpectation>
): void => {
  if (!isClient()) return;

  try {
    writeToLocalStorage(
      null,
      LocalStorageKeys.TRANSCRIPT_EXPECTATIONS,
      JSON.stringify(expectations)
    );
  } catch {
    discardTranscriptExpectations();
  }
};

const isValidBackgroundActivity = (activity: any): boolean => {
  return (
    typeof activity === 'object' &&
    activity !== null &&
    typeof activity.id === 'string' &&
    typeof activity.targetKey === 'string' &&
    typeof activity.status === 'string' &&
    typeof activity.target === 'object' &&
    activity.target !== null
  );
};

const discardBackgroundActivities = (): void => {
  try {
    removeFromLocalStorage(null, LocalStorageKeys.BACKGROUND_ACTIVITIES);
  } catch {
    // storage is unavailable, nothing left to clean up
  }
};

export const saveBackgroundActivities = (activities: Record<string, unknown>): void => {
  if (!isClient()) return;

  try {
    writeToLocalStorage(null, LocalStorageKeys.BACKGROUND_ACTIVITIES, JSON.stringify(activities));
  } catch {
    discardBackgroundActivities();
  }
};

export const loadBackgroundActivities = (): Record<string, any> => {
  if (!isClient()) return {};

  try {
    const raw = readFromLocalStorage(null, LocalStorageKeys.BACKGROUND_ACTIVITIES);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([, activity]) =>
        isValidBackgroundActivity(activity)
      )
    );
  } catch {
    discardBackgroundActivities();
    return {};
  }
};

export const loadTranscriptExpectations = (): Record<number, TranscriptExpectation> => {
  if (!isClient()) return {};

  try {
    const raw = readFromLocalStorage(null, LocalStorageKeys.TRANSCRIPT_EXPECTATIONS);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([, expectation]) =>
        isValidTranscriptExpectation(expectation)
      )
    ) as Record<number, TranscriptExpectation>;
  } catch {
    discardTranscriptExpectations();
    return {};
  }
};
